import { assertNotEmpty } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { Td, Tr } from '@festina-life/ui/listsAndTables'

import { useTemplateContext } from '../../context/templateContext'
import { isNotInherited } from '../../uiModels/templateOperations/types'
import { createLiteralTypeInformation } from '../fplusExpression/expressionType'
import { InheritanceIndicator } from '../inheritanceIndicator/InheritanceIndicator'
import { TemplateParentLink } from '../templateParentLink/TemplateParentLink'
import { TemplateReferenceFieldValueWithError } from './TemplateReferenceFieldValueWithError'
import type { TemplateReferenceTableRowProps } from './types'

export const TemplateReferenceExpresssionsTableRow = ({
  data,
  fieldPath,
  onChange,
  errors,
}: TemplateReferenceTableRowProps) => {
  const translate = useTranslate()
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  assertNotEmpty(templateVersion)

  const variantPath = `${fieldPath}[${data.source}]`

  const handleRemoveVariant = async () => {
    if (onChange) {
      await onChange(null)
    }
  }

  return (
    <Tr
      key={variantPath}
      // @beta hack to make the TD contents fill the full row height
      // https://stackoverflow.com/questions/3215553/make-a-div-fill-an-entire-table-cell
      className="relative size-full h-px align-top"
    >
      <Td className="relative flex h-full items-center justify-between">
        {data.source ? <TemplateParentLink templateId={data.source} /> : '-'}
        <InheritanceIndicator value={data} />
      </Td>
      <Td className="h-[inherit] w-1/4">
        <TemplateReferenceFieldValueWithError
          data={data}
          fieldName="defaultSelected"
          onChange={onChange}
          errors={(errors ?? { defaultSelected: [] })['defaultSelected']}
          typeInformation={createLiteralTypeInformation({
            literalType: 'boolean',
            source: data.defaultSelected?.expression?.fplusSource,
          })}
        />
      </Td>
      <Td className="h-[inherit] w-1/4">
        <TemplateReferenceFieldValueWithError
          data={data}
          fieldName="selectable"
          onChange={onChange}
          errors={(errors ?? { selectable: [] })['selectable']}
          typeInformation={createLiteralTypeInformation({
            literalType: 'boolean',
            source: data.selectable?.expression?.fplusSource,
          })}
        />
      </Td>
      <Td className="h-[inherit] w-1/4">
        <TemplateReferenceFieldValueWithError
          data={data}
          fieldName="cardinality"
          onChange={onChange}
          errors={(errors ?? { cardinality: [] })['cardinality']}
        />
      </Td>
      {isDraftTemplate && isNotInherited(data) && onChange && (
        <Td className="h-[inherit]">
          <IconButton
            onClick={handleRemoveVariant}
            iconSize="md"
            tip={translate('action.remove')}
            iconName="MinusCircle"
          />
        </Td>
      )}
    </Tr>
  )
}
